import React, {useRef} from 'react'
import {FormattedMessage} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'
import {Box, Flex, Text, useStyleConfig} from '@chakra-ui/react'
import {useProduct} from '@salesforce/commerce-sdk-react'
// import PaperlessStickyMenu from './partials/paperless-sticky-menu'
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import Dropdown from '../../components/dropdown'
import HeadingSection from '../../components/heading-section'
import DynamicImage from '../../components/dynamic-image'
import ProductManuals from './partials/product-manuals'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
import {parseAndSanitizeJSONWithSpaces} from '../../utils/utils'
import {useLocaleLanguage} from '../../hooks/use-locale-custom'
import {PromoSliderVideo} from '../../components/editorial-components'
import RecommendedProducts from '../../components/recommended-products'
import SectionIconbox from '../../components/editorial-components/section-iconbox'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const Paperless = () => {
    const styles = useStyleConfig('Paperless')
    const sliderRef = useRef(null)
    // const [showMenu, setShowMenu] = useState(false) // commented until R2 release
    const {productId} = useParams()
    const {data: contentstackData} = useGetPageByType('paperless')
    const {currency: activeCurrency} = useCurrency()

    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)

    const {
        data: product,
        isError: isProductError,
        error: productError,
    } = useProduct({
        parameters: {
            id: urlParams.get('pid') || productId,
            allImages: true,
            currency: activeCurrency,
        },
    })

    const isFromQr = urlParams.get('lead') === 'qr'

    const currentLanguage = useLocaleLanguage()
    const languageMap = {
        en: 'English',
        it: 'Italian',
        fr: 'French',
        de: 'German',
        es: 'Spanish',
        pt: 'Portuguese',
        nl: 'Dutch',
    }
    const fallbackOption = {label: 'English', value: 'English'}
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const languageOptions = Object.entries(languageMap).map(([code, label]) => ({
        label,
        value: label,
    }))
    const defaultOption =
        languageOptions.find(
            (option) => option.label.toLowerCase() === languageMap[currentLanguage]?.toLowerCase(),
        ) || fallbackOption

    const productManuals =
        product &&
        product?.c_manualDocument &&
        parseAndSanitizeJSONWithSpaces(product?.c_manualDocument, product?.c_subModelIdentifier)

    if (isProductError) {
        const errorStatus = productError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Product Not Found.')
            default:
                throw new HTTPError(`HTTP Error ${errorStatus} occurred.`)
        }
    }

    // commented until R2 release
    // useEffect(() => {
    //     const handleScroll = () => {
    //         if (
    //             sliderRef &&
    //             sliderRef.current &&
    //             sliderRef.current.getBoundingClientRect().top <= 0
    //         )
    //             setShowMenu(true)
    //         else setShowMenu(false)
    //     }
    //     window.addEventListener('scroll', handleScroll)
    //     return () => window.removeEventListener('scroll', handleScroll)
    // }, [])

    const productRegSection = contentstackData?.product_registration_component?.[0]

    const displayIdentifier = isFromQr ? product?.c_leadIdentifier || product?.name : product?.name
    const displayNumber = isFromQr
        ? product?.c_leadNumber || product?.c_subModelIdentifier
        : product?.c_subModelIdentifier

    return (
        <Flex {...styles.pageContainer} data-testid="paperless-page">
            {/* <Slide // commented until R2 release
                in={showMenu}
                data-testid="paperless-sticky-menu-slider"
                aria-hidden={!showMenu}
                direction="top"
                style={{zIndex: 10, top: 0, width: '100%'}}
            >
                <PaperlessStickyMenu product={product} />
            </Slide> */}
            <HeadingSection
                title={
                    <FormattedMessage
                        defaultMessage="{productName} Support Center"
                        id="paperless-page.heading-title"
                        values={{productName: displayIdentifier}}
                    />
                }
                description={
                    <FormattedMessage
                        defaultMessage="All you need to set up and get the most from your product."
                        id="paperless-page.heading-subtitle"
                    />
                }
                styles={styles}
                ref={sliderRef}
            />
            <Flex {...styles.productSection}>
                {product && product?.imageGroups && (
                    <Flex {...styles.productInfo} data-testid="product-info">
                        <Box {...styles.imageContainer}>
                            <DynamicImage
                                src={(() => {
                                    const matchingImageGroup = product?.imageGroups?.find((group) =>
                                        group?.variationAttributes?.some(
                                            (attr) =>
                                                attr.id === 'subModelIdentifier' &&
                                                attr.values?.some(
                                                    (value) =>
                                                        value.value ===
                                                        product?.c_subModelIdentifier,
                                                ),
                                        ),
                                    )
                                    return (
                                        matchingImageGroup?.images[0]?.link ||
                                        matchingImageGroup?.images[0]?.disBaseLink ||
                                        product?.imageGroups[0]?.images[0]?.link ||
                                        product?.imageGroups[0]?.images[0]?.disBaseLink
                                    )
                                })()}
                            />
                        </Box>
                        <Flex {...styles.productDetails}>
                            <Text {...styles.productDetailsTitle}>{displayIdentifier}</Text>
                            <Text {...styles.productDetailsId}>{displayNumber}</Text>
                        </Flex>
                    </Flex>
                )}
                <Flex {...styles.productManualsSection}>
                    <Flex {...styles.productManualsHeader}>
                        <Text {...styles.headingTitle}>
                            <FormattedMessage
                                defaultMessage="Manuals and more"
                                id="paperless-page.product-section.heading"
                            />
                        </Text>
                        <Flex {...styles.dropdownContainer}>
                            <Dropdown
                                name=""
                                label=""
                                placeholder="Select your language"
                                isSearchable
                                //? Countries are hardcoded for now, but this can be fetched from an API
                                value={defaultOption}
                                options={languageOptions}
                                variant="dropdownTextStyle"
                            />
                        </Flex>
                    </Flex>
                    {productManuals ? (
                        <ProductManuals product={product} productManuals={productManuals} />
                    ) : (
                        <Text {...styles.headingTitle} textAlign="center">
                            <FormattedMessage
                                defaultMessage="Sorry, no Manuals for this product yet"
                                id="paperless-page.no-manuals-message"
                            />
                        </Text> //?Placeholder
                    )}
                </Flex>
            </Flex>
            {productRegSection && (
                <SectionIconbox
                    title={productRegSection?.title}
                    cards={productRegSection?.section_items}
                    button={productRegSection?.button}
                />
            )}
            {product?.c_youtubeVideoHowTo && (
                <PromoSliderVideo
                    sliderGroup={parseAndSanitizeJSONWithSpaces(
                        product?.c_youtubeVideoHowTo,
                        product?.c_subModelIdentifier,
                    )?.map((video) => ({
                        text_block: {
                            title: video.title,
                        },
                        video_link: {
                            href: video.videolink,
                        },
                        thumbnail: video.medialink ? {src: video.medialink} : {},
                    }))}
                />
            )}
            <Flex
                background="background.light"
                justifyContent="center"
                position="relative"
                overflow={'hidden'}
            >
                <RecommendedProducts
                    products={(product?.c_recommendations || product?.recommendations || [])
                        .filter((rec) => rec?.recommendationType?.value === 2)
                        .map((rec) => rec.recommendedItemId)}
                    title={
                        <FormattedMessage
                            defaultMessage="Frequently Bought Together"
                            id="recommended_product_card_variant.title"
                        />
                    }
                    variant={'greyBackgroundVariant'}
                    tileImageVariant={'transparent'}
                    prefix="frequently-bought-together"
                    gtmCarouselName="carousel_pdp"
                />
            </Flex>
            <Box>
                {contentstackData?.editorial_components.map((component) => (
                    <EditorialComponentsFactory
                        key={component.uid}
                        componentType={component?._content_type_uid}
                        {...component}
                    />
                ))}
            </Box>
        </Flex>
    )
}

Paperless.getTemplateName = () => 'paperless-page'
export default Paperless
